import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";
import { useEffect, useState } from "react";

/** 入力の種類。 */
export type SingleFieldKind = "text" | "textarea" | "date" | "number" | "select";

export interface SingleFieldOption {
  key: string;
  label: string;
}

/** 編集対象の 1 項目。 */
export interface SingleFieldTarget {
  /** ダイアログの見出しに出す項目名（例: 取引先コード）。 */
  label: string;
  /** 現在値。未設定は null。select は選択肢の key、date は YYYY-MM-DD。 */
  value: string | null;
  /** 必須項目なら空で保存できない。 */
  required?: boolean;
  /** 既定は "text"。 */
  kind?: SingleFieldKind;
  /** kind が "select" のときの選択肢。 */
  options?: SingleFieldOption[];
  /** 入力欄の下に出す補足。 */
  description?: string;
}

/** select で「未設定に戻す」を表す内部キー（空文字は HeroUI が扱えないため）。 */
const NONE_KEY = "__none__";

/**
 * 一覧のセルや詳細画面の項目から開く「1 項目だけ編集する」ダイアログ。
 *
 * ngtg_msc の `showSingleFieldEditDialog` に対応する。呼び出し側が開閉状態と対象を
 * 1 つだけ持ち、このダイアログを 1 つだけ描く（セルごとに Modal を持たせると
 * 行数 × 列数ぶんの Modal が生まれるため）。
 *
 * 任意項目を空にして保存すると未入力（null）に戻す。`onSave` はエラー文言を
 * 返すとダイアログを閉じずにその場で表示する（重複コードなど、入力し直せば
 * 解決する失敗を画面の外に出さないため）。
 */
export default function SingleFieldEditDialog({
  target,
  onClose,
  onSave,
}: {
  /** null のとき閉じている。 */
  target: SingleFieldTarget | null;
  onClose: () => void;
  /** 保存する。エラー文言を返すと閉じずに表示する。 */
  onSave: (value: string | null) => Promise<string | null>;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 対象が変わるたびに現在値を入れ直す（前の項目の入力を持ち越さない）。
  useEffect(() => {
    setText(target?.value ?? "");
    setError(null);
    setSaving(false);
  }, [target]);

  if (!target) return null;

  const kind = target.kind ?? "text";
  const trimmed = text.trim();
  const isEmpty = trimmed.length === 0;

  const save = async () => {
    if (target.required && isEmpty) {
      setError(`${target.label}は必須です。`);
      return;
    }
    setSaving(true);
    // 任意項目の空は「未入力に戻す」として null を送る。
    const message = await onSave(isEmpty ? null : trimmed);
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  const description =
    target.description ??
    (target.required ? "必須項目です。" : "空にすると未入力に戻します。");

  const common = {
    autoFocus: true,
    "aria-label": target.label,
    variant: "bordered" as const,
    radius: "sm" as const,
    isInvalid: error != null,
    errorMessage: error ?? undefined,
    description,
  };

  const change = (v: string) => {
    setText(v);
    setError(null);
  };

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()} size="md">
      <ModalContent>
        <ModalHeader>{target.label}</ModalHeader>
        <ModalBody>
          {kind === "select" ? (
            <Select
              {...common}
              selectedKeys={[text || NONE_KEY]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const key = [...keys][0] as string | undefined;
                change(!key || key === NONE_KEY ? "" : key);
              }}
            >
              {[
                // 必須でない項目は「未設定」に戻せるようにする。
                ...(target.required ? [] : [{ key: NONE_KEY, label: "（未設定）" }]),
                ...(target.options ?? []),
              ].map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>
          ) : kind === "textarea" ? (
            <Textarea {...common} minRows={3} value={text} onValueChange={change} />
          ) : (
            <Input
              {...common}
              type={kind === "date" ? "date" : kind === "number" ? "number" : "text"}
              value={text}
              onValueChange={change}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
              }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="save"
            isLoading={saving}
            onPress={() => void save()}
          >
            保存
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
